import { ReactNode } from 'react';
import { Stack, Text, Button, ThemeIcon } from '@mantine/core';
import { IconUsersGroup, IconSearchOff, IconPlus, TablerIconsProps } from '@tabler/icons-react';

export interface EmptyStateProps {
  /** Switches icon/copy: 'no-data' = nothing exists yet, 'no-results' = filters/search matched nothing. */
  variant?: 'no-data' | 'no-results';
  /** Noun used in generated copy, e.g. "leaders", "facilitators". */
  entityLabel?: string;
  /** Custom Icon component (e.g., Tabler Icon) */
  icon?: React.ComponentType<TablerIconsProps>;
  /** Custom title string */
  title?: string;
  /** Custom description string or node */
  description?: ReactNode;
  /** Shown only for variant="no-data" — e.g. "Add Leader". Omit to hide the action. */
  onAdd?: () => void;
  addLabel?: string;
  /** Shown only for variant="no-results" — clears search/filters. */
  onClearFilters?: () => void;
}

export function EmptyState({
  variant = 'no-data',
  entityLabel = 'items',
  icon: CustomIcon,
  title,
  description,
  onAdd,
  addLabel,
  onClearFilters,
}: EmptyStateProps) {
  const isNoData = variant === 'no-data';

  // Fallback icon selection
  const DefaultIcon = isNoData ? IconUsersGroup : IconSearchOff;
  const IconComponent = CustomIcon ?? DefaultIcon;

  // Fallback text resolution
  const resolvedTitle =
    title ?? (isNoData ? `No ${entityLabel} yet` : `No ${entityLabel} match your search`);

  const resolvedDescription =
    description ??
    (isNoData
      ? `Once ${entityLabel} are added, they'll show up here.`
      : 'Try adjusting your search term or filters.');

  return (
    <Stack align="center" gap="xs" py="xl">
      <ThemeIcon variant="light" color="gray" size={56} radius="xl">
        <IconComponent size={28} />
      </ThemeIcon>

      <Text fw={600} size="md">
        {resolvedTitle}
      </Text>

      <Text size="sm" c="dimmed" ta="center" maw={360}>
        {resolvedDescription}
      </Text>

      {isNoData && onAdd && (
        <Button mt="sm" leftSection={<IconPlus size={16} />} onClick={onAdd}>
          {addLabel ?? 'Add'}
        </Button>
      )}

      {!isNoData && onClearFilters && (
        <Button mt="sm" variant="light" onClick={onClearFilters}>
          Clear Filters
        </Button>
      )}
    </Stack>
  );
}

export default EmptyState;